import { CircularProgress, Link, Snackbar } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import { useAbundanceEngineGetViewPathDetails } from '../../../../../api/abundanceEngine'
import { createAbundanceEngineView, editAbundanceEngineView } from '../../../../../service'
import Alert from '../../../../atoms/Alert'
import SydButton from '../../../../commonDesign/Button'
import ViewContentContainer from './ViewContentContainer'
import ViewDetailInputs from './ViewDetailInputs'

function AbundanceEngineEditView () {
  const history = useHistory()
  const params = useParams()
  const isEdit = useMemo(() => params.path?.length > 0, [params.path])
  const [alert, setAlert] = useState(null)
  const contentContainerRef = useRef(null)
  const [initialContent, setInitialContent] = useState('')

  const form = useForm({
    defaultValues: {
      path: '',
      name: '',
      version: 1,
      description: '',
      content: '',
      isLive: false,
      hideTitle: false,
      disabledViewId: null,
      enablementCodeId: null
    }
  })

  const { data, isFetching } = useAbundanceEngineGetViewPathDetails({ path: params.path }, {
    enabled: isEdit && !!params.path?.length
  })

  useEffect(() => {
    if (isEdit && data) {
      form.reset({
        ...data,
        showInNavigation: !!data.showInNavigation,
        isLive: !!data.isLive,
        hideTitle: !!data.hideTitle
      })
      setInitialContent(data.content || '')
    }
  }, [data, form, isEdit])

  const { handleSubmit } = form

  const onSubmit = useCallback(async (formData) => {
    try {
      // Get the latest content from the editor ref to ensure we have the most recent changes
      // This is important because the form state is debounced for performance
      const latestContent = contentContainerRef.current?.getEditorValue?.() || formData.content
      const dataToSubmit = { ...formData, content: latestContent }

      if (isEdit) {
        if (!dataToSubmit?.viewId) {
          console.error('View ID is required for editing a view')
          return
        }
        const { data } = await editAbundanceEngineView(dataToSubmit?.viewId, dataToSubmit)
        if (data.path !== params.path) {
          history.replace(`/admin/abundance-engine-views/edit/${data.path}`)
        }
        return setAlert({ severity: 'success', message: 'View updated successfully' })
      } else {
        const { data } = await createAbundanceEngineView(dataToSubmit)
        history.push(`/admin/abundance-engine-views/edit/${data.path}`)
        return setAlert({ severity: 'success', message: 'View created successfully' })
      }
    } catch (err) {
      console.error(err)
      setAlert({ severity: 'error', message: err?.message || 'An error occurred' })
    }
  }, [history, isEdit, params.path])

  return (
    <>
      <Snackbar
        open={!!alert}
        autoHideDuration={6000}
        onClose={() => setAlert(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {!!alert && (
          <Alert onClose={() => setAlert(null)} severity={alert?.severity}>
            {alert?.message}
          </Alert>
        )}
      </Snackbar>

      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className='h-full relative'
          style={{ maxWidth: 'calc(100vw - 50px)', ...((isFetching || form.formState.isSubmitting) && { opacity: 0.5, pointerEvents: 'none' }) }}
        >
          {(isFetching || form.formState.isSubmitting) && (
            <div
              className='absolute'
              style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999 }}
            >
              <CircularProgress />
            </div>
          )}
          <div className='flex gap-5 p-4 px-5 w-full h-full'>
            <div
              className='flex flex-col gap-5 relative'
              style={{ width: '300px' }}
            >
              <div>
                <p className='m-0 font-bold font-size-lg'>View Details</p>
                <p className='m-0 font-light font-size-sm'>
                  {isEdit ? 'Edit the details of this view.' : 'Create a new view by filling in the details below.'}
                </p>
              </div>
              <div className='flex-1'>
                <ViewDetailInputs />
              </div>
              <div className='flex justify-between'>
                <Link to='/admin/abundance-engine-views'>
                  <SydButton variant='secondary' type='button'>
                    Cancel
                  </SydButton>
                </Link>

                <SydButton
                  type='submit'
                  processing={form.formState.isSubmitting}
                  disabled={form.formState.isSubmitting || isFetching}
                >
                  {isEdit ? 'Save' : 'Create'} View
                </SydButton>
              </div>
            </div>

            <div className='flex-1 w-full' style={{ overflow: 'hidden' }}>
              <ViewContentContainer
                ref={contentContainerRef}
                jsxContent={initialContent}
                viewId={form.getValues('viewId')}
                onJsxContentChange={(jsx) => {
                  form.setValue('content', jsx, { shouldDirty: true })
                  setInitialContent(jsx)
                }}
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  )
}

export default AbundanceEngineEditView
